/* ======================================================================
   WINKLER — main-collection component
   ----------------------------------------------------------------------
   Loaded by sections/main-collection.liquid. Three responsibilities:

   1. Toolbar dropdowns (.wk-dd, native <details>): one open at a time,
      close on outside click / Escape / selection. Filter + sort changes
      themselves are handled by Prestige's <facets-form update-on-change>.
   2. Price buckets: a bucket button writes the hidden gte/lte inputs and
      fires a change event so facets-form submits.
   3. Load more: fetches the next page's section render and APPENDS its
      cards (the design's "טעינת עוד יצירות"), instead of paginating.

   All listeners are delegated on `document` because facet updates replace
   the whole section's DOM.
   ====================================================================== */
(() => {
  if (window.__wkMainCollectionInit) return;
  window.__wkMainCollectionInit = true;

  const DD = "details[data-wk-dd]";

  const closeAll = (except) => {
    document.querySelectorAll(`${DD}[open]`).forEach((dd) => {
      if (dd !== except) dd.removeAttribute("open");
    });
  };

  // only one dropdown open at a time ('toggle' doesn't bubble → capture phase)
  document.addEventListener(
    "toggle",
    (event) => {
      const dd = event.target.closest?.(DD);
      if (dd && dd.open) closeAll(dd);
    },
    true
  );

  // close on outside click
  document.addEventListener("click", (event) => {
    if (!event.target.closest?.(DD)) closeAll();
  });

  // close on Escape
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeAll();
  });

  // picking an option closes its dropdown (facets-form reacts to the same change event)
  document.addEventListener("change", (event) => {
    const dd = event.target.closest?.(DD);
    if (dd && event.target.matches("input")) dd.removeAttribute("open");
  });

  // price bucket → write the hidden gte/lte inputs, then let facets-form submit
  document.addEventListener("click", (event) => {
    const bucket = event.target.closest?.("[data-wk-price]");
    if (!bucket) return;

    const form = bucket.closest("form");
    const gte = form?.querySelector('input[name$="price.gte"]');
    const lte = form?.querySelector('input[name$="price.lte"]');
    if (!gte || !lte) return;

    gte.value = bucket.dataset.wkPriceGte || "";
    lte.value = bucket.dataset.wkPriceLte || "";
    bucket.closest(DD)?.removeAttribute("open");
    gte.dispatchEvent(new Event("change", { bubbles: true }));
  });

  // load more — append the next page's cards without replacing the grid
  document.addEventListener("click", async (event) => {
    const button = event.target.closest?.("[data-wk-loadmore-btn]");
    if (!button) return;
    event.preventDefault();
    if (button.getAttribute("aria-busy") === "true") return;

    const section = button.closest(".shopify-section");
    const url = new URL(button.href, window.location.origin);
    url.searchParams.set("section_id", button.dataset.sectionId);

    button.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(url.toString());
      const doc = new DOMParser().parseFromString(await response.text(), "text/html");

      const newList = doc.querySelector(".product-list");
      const list = section.querySelector(".product-list");
      if (newList && list) {
        const imported = document.importNode(newList, true);
        // Prestige's <product-list> reveals cards only ONCE, when it first scrolls
        // into view — cards appended later would stay at the reveal state's opacity 0.
        // Reveal them here instead, mirroring the stock animation (20px rise, 50ms
        // stagger capped like the design prototype's load-more).
        const fresh = [...imported.querySelectorAll('[reveal-on-scroll="true"]')];
        list.append(...imported.childNodes);

        const motionSafe = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        fresh.forEach((el, index) => {
          el.removeAttribute("reveal-on-scroll");
          if (motionSafe && el.animate) {
            el.animate(
              { opacity: [0, 1], transform: ["translateY(20px)", "translateY(0)"] },
              { duration: 200, easing: "ease-in-out", delay: Math.min(index, 7) * 50, fill: "backwards" }
            );
          }
        });
      }

      // "showing X of Y" — the fetched page's number IS the new shown total
      const shown = section.querySelector("[data-wk-shown]");
      const newShown = doc.querySelector("[data-wk-shown]");
      if (shown && newShown) shown.textContent = newShown.textContent;

      // swap (or drop) the load-more row for the fetched page's one
      const wrap = section.querySelector("[data-wk-loadmore]");
      const newWrap = doc.querySelector("[data-wk-loadmore]");
      if (wrap) {
        if (newWrap) {
          wrap.replaceWith(document.importNode(newWrap, true));
        } else {
          wrap.remove();
        }
      }
    } catch (error) {
      // network hiccup — fall back to classic navigation
      window.location.href = button.href;
    } finally {
      button.removeAttribute("aria-busy");
    }
  });
})();

/* ======================================================================
   WINKLER — mobile filters & sort drawer (storyonline-style, ≤699px)
   ----------------------------------------------------------------------
   Markup in sections/main-collection.liquid ([data-wk-mfacets]). The
   drawer's inputs live in the same <facets-form> form as the desktop
   dropdowns, so a change re-renders the whole section through the theme's
   AJAX faceting. This block:

   1. Opens/closes the drawer and its per-filter drill-in submenus.
   2. Keeps the drawer (and the active submenu + scroll offsets) open
      across the section re-render: theme.js dispatches theme:loading:end
      and then, in the same task, replaces the section's children — a
      microtask queued from that event runs on the fresh DOM.
   3. Disables whichever facet input set is off-viewport (the desktop
      dropdowns on mobile, this drawer on desktop) so the shared form
      never submits duplicated params. Server-disabled inputs (zero-count
      values) are left alone via the data-wk-mq-disabled marker.

   Per-group "ניקוי" unchecks that group only; "מההתחלה" is a facet-link
   to the bare collection URL (theme-native clear-all). The sort <select>
   is name="sort_by" — its change event is handled by facets-form itself.
   ====================================================================== */
(() => {
  if (window.__wkMobileFacetsInit) return;
  window.__wkMobileFacetsInit = true;

  const mq = window.matchMedia("(max-width: 699px)");
  const state = { open: false, sub: null, mainScroll: 0, subScroll: 0 };

  const drawer = () => document.querySelector("[data-wk-mfacets]");

  const syncDisabled = () => {
    const mobile = mq.matches;
    const toggle = (els, disable) =>
      els.forEach((el) => {
        if (disable) {
          if (!el.disabled) {
            el.disabled = true;
            el.dataset.wkMqDisabled = "1";
          }
        } else if (el.dataset.wkMqDisabled) {
          el.disabled = false;
          delete el.dataset.wkMqDisabled;
        }
      });
    toggle(document.querySelectorAll('[data-wk-desktop-facets] input:not([type="hidden"])'), mobile);
    toggle(document.querySelectorAll('[data-wk-mfacets] input:not([type="hidden"]), [data-wk-mfacets] select'), !mobile);
  };

  const lockScroll = (lock) => document.documentElement.classList.toggle("wk-mfacets-lock", lock);

  const setOpen = (el, open) => {
    if (!el) return;
    el.classList.toggle("is-open", open);
    lockScroll(open);
    state.open = open;
    if (!open) {
      state.sub = null;
      el.querySelectorAll("[data-wk-mgroup].is-active").forEach((group) => group.classList.remove("is-active"));
    }
  };

  document.addEventListener("click", (event) => {
    const target = event.target;

    if (target.closest?.("[data-wk-mfacets-open]")) {
      setOpen(drawer(), true);
      return;
    }

    const closer = target.closest?.("[data-wk-mfacets-close]");
    if (closer) {
      setOpen(closer.closest("[data-wk-mfacets]"), false);
      return;
    }

    const groupOpen = target.closest?.("[data-wk-mgroup-open]");
    if (groupOpen) {
      const group = groupOpen.closest("[data-wk-mgroup]");
      group.classList.add("is-active");
      state.sub = group.dataset.wkMgroupKey;
      return;
    }

    const groupClose = target.closest?.("[data-wk-mgroup-close]");
    if (groupClose) {
      groupClose.closest("[data-wk-mgroup]")?.classList.remove("is-active");
      state.sub = null;
      return;
    }

    const groupClear = target.closest?.("[data-wk-mgroup-clear]");
    if (groupClear) {
      const group = groupClear.closest("[data-wk-mgroup]");
      let changed = null;

      group.querySelectorAll('input[type="checkbox"]:checked').forEach((box) => {
        box.checked = false;
        changed = box;
      });

      // the price group has no checkboxes — its buckets write the shared hidden inputs
      if (group.querySelector("[data-wk-price]")) {
        const form = group.closest("form");
        const gte = form?.querySelector('input[name$="price.gte"]');
        const lte = form?.querySelector('input[name$="price.lte"]');
        if (gte && lte && (gte.value || lte.value)) {
          gte.value = "";
          lte.value = "";
          changed = gte;
        }
      }

      if (changed) changed.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });

  // Escape backs out of the submenu first, then closes the drawer
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !state.open) return;
    const el = drawer();
    const active = el?.querySelector("[data-wk-mgroup].is-active");
    if (active) {
      active.classList.remove("is-active");
      state.sub = null;
    } else {
      setOpen(el, false);
    }
  });

  // remember scroll offsets right before the theme replaces the section's DOM
  document.addEventListener("facet:update", () => {
    const el = drawer();
    if (!el || !state.open) return;
    state.mainScroll = el.querySelector("[data-wk-mfacets-main]")?.scrollTop || 0;
    state.subScroll = el.querySelector("[data-wk-mgroup].is-active [data-wk-msub-scroll]")?.scrollTop || 0;
  });

  // restore the drawer on the freshly-rendered section (see the header comment)
  document.addEventListener("theme:loading:end", () => {
    queueMicrotask(() => {
      syncDisabled();
      const el = drawer();
      if (!el || !state.open) return;

      el.classList.add("no-anim", "is-open");
      lockScroll(true);

      if (state.sub) {
        const group = el.querySelector(`[data-wk-mgroup][data-wk-mgroup-key="${CSS.escape(state.sub)}"]`);
        if (group) {
          group.classList.add("is-active");
          const scroll = group.querySelector("[data-wk-msub-scroll]");
          if (scroll) scroll.scrollTop = state.subScroll;
        } else {
          state.sub = null;
        }
      }

      const main = el.querySelector("[data-wk-mfacets-main]");
      if (main) main.scrollTop = state.mainScroll;

      requestAnimationFrame(() => requestAnimationFrame(() => el.classList.remove("no-anim")));
    });
  });

  if (mq.addEventListener) {
    mq.addEventListener("change", syncDisabled);
  } else {
    mq.addListener(syncDisabled); // iOS < 14
  }
  syncDisabled();
})();
